import { BitReader, BitWriter } from "../bitstream.ts";
import { INPUT_REDUNDANCY, INTERP_DELAY_TICKS, PROTOCOL_VERSION, TICK_RATE, Team } from "../constants.ts";
import { HERO_LIST } from "../heroes/defs.ts";
import type { HeroId } from "../heroes/types.ts";
import type { MapData } from "../map/mapdata.ts";
import { lerp, lerpAngle } from "../math.ts";
import { type InputCmd, quantizeInput, seqGreater } from "../net/input.ts";
import type { ClientMsg, LobbyState, MatchSummary, ServerMsg } from "../net/messages.ts";
import { applyLocal, MATCH_NET_ID } from "../net/replicate.ts";
import { type AnyFields, decodeRecord, EntityType, MSG_SNAPSHOT, readSnapshot, type SnapshotPacket, type WorldRecords, writeInputPacket } from "../net/snapshot.ts";
import type { SimEvent } from "../sim/events.ts";
import { Sim } from "../sim/sim.ts";
import { convoyWorld } from "../game/convoy.ts";
import { constructObstaclesFromDecoded } from "../sim/constructs.ts";
import type { Phase } from "../game/match.ts";
import type { GameMode } from "../map/maps.ts";

export interface ClientTransport {
  sendReliable(msg: ClientMsg): void;
  sendUnreliable(data: Uint8Array): void;
  onReliable?: (msg: ServerMsg) => void;
  onUnreliable?: (data: Uint8Array) => void;
  onClose?: () => void;
  close(): void;
}

export interface InputSample {
  buttons: number;
  moveX: number;
  moveZ: number;
  yaw: number;
  pitch: number;
}

export interface InterpEntity {
  netId: number;
  type: EntityType;
  f: AnyFields;
}

interface StoredSnap {
  tick: number;
  world: WorldRecords;
  decoded: Map<number, AnyFields>;
}

export interface NetStats {
  rtt: number;
  snapshotsPerSec: number;
  bytesPerSec: number;
  lastCorrection: number;
  corrections: number;
  pendingInputs: number;
  serverQueue: number;
  timeScale: number;
  lossPct: number;
}

const SNAP_BUFFER = 32;

/**
 * Client-side netcode: input upload, prediction + reconciliation of the local player,
 * snapshot buffering and interpolation of everything else. DOM-free (used by the browser
 * client and the headless test harness).
 */
export class NetClient {
  predict: Sim;
  localEid: number | undefined;
  localNetId = 0;
  you = 0;
  lobby: LobbyState | undefined;
  build = "";
  /**
   * The server speaks our protocol: known from its welcome. Until then, and for a server from
   * another version (whose snapshots would decode as garbage), snapshots are ignored.
   */
  compatible = false;
  serverTick = 0;
  onLobby?: (l: LobbyState) => void;
  onChat?: (from: string, text: string) => void;
  onNotice?: (text: string, error: boolean) => void;
  onMatchEnd?: (s: MatchSummary) => void;
  onWelcome?: (m: Extract<ServerMsg, { t: "welcome" }>) => void;
  /** Called for each new server event (deduplicated, in order). */
  onEvent?: (e: SimEvent) => void;
  /** Local prediction events (own tracers/casts). */
  onPredictedEvent?: (e: SimEvent) => void;
  /** Correction vector (old predicted − new predicted position) after reconciliation. */
  onCorrection?: (dx: number, dy: number, dz: number) => void;

  private seq = 0;
  private pending: InputCmd[] = [];
  private sentHistory: InputCmd[] = [];
  private snaps: StoredSnap[] = [];
  private worlds = new Map<number, WorldRecords>();
  private latestTick = 0;
  private tickOffset: number | undefined;
  private accumulator = 0;
  private seenEvents = new Set<string>();
  private seenOrder: string[] = [];
  private startTime: number;
  timeScale = 1;
  private lastQueue = 2;
  private constructDecoded = new Map<number, AnyFields>();
  private statWindow = { start: 0, snaps: 0, bytes: 0, missing: 0, lastTick: 0 };
  stats: NetStats = { rtt: 0, snapshotsPerSec: 0, bytesPerSec: 0, lastCorrection: 0, corrections: 0, pendingInputs: 0, serverQueue: 0, timeScale: 1, lossPct: 0 };
  private lastRttReport = 0;
  latestLocal?: SnapshotPacket["local"];

  constructor(
    map: MapData,
    readonly transport: ClientTransport,
    private readonly now: () => number = () => performance.now(),
  ) {
    this.predict = new Sim(map, { authority: false });
    this.startTime = now();
    this.statWindow.start = now();
    transport.onReliable = (m) => this.onReliable(m);
    transport.onUnreliable = (d) => this.onPacket(d);
  }

  /**
   * Switches the prediction world to another map (the room changed mode). The connection, input
   * sequence and snapshot history carry on; the local player is re-created from the next snapshot.
   */
  setMap(map: MapData) {
    this.predict.physics.free();
    this.predict = new Sim(map, { authority: false });
    this.localEid = undefined;
    this.localNetId = 0;
    this.pending = [];
    this.latestLocal = undefined;
  }

  hello(name: string, token: string, build: string) {
    this.transport.sendReliable({ t: "hello", name, token, build });
  }

  send(msg: ClientMsg) {
    this.transport.sendReliable(msg);
  }

  private clientTime() {
    return this.now() - this.startTime;
  }

  // ---------------------------------------------------------------- reliable

  private onReliable(m: ServerMsg) {
    switch (m.t) {
      case "welcome":
        this.you = m.you;
        this.build = m.build;
        this.compatible = m.protocol === PROTOCOL_VERSION;
        this.onWelcome?.(m);
        break;
      case "lobby":
        this.lobby = m.lobby;
        this.onLobby?.(m.lobby);
        break;
      case "chat":
        this.onChat?.(m.from, m.text);
        break;
      case "matchEnd":
        this.onMatchEnd?.(m.summary);
        break;
      case "error":
        this.onNotice?.(m.message, true);
        break;
      case "notice":
        this.onNotice?.(m.message, false);
        break;
    }
  }

  // ---------------------------------------------------------------- snapshots

  private onPacket(data: Uint8Array) {
    if (!this.compatible) return;
    const r = new BitReader(data);
    if (r.u8() !== MSG_SNAPSHOT) return;
    let res: ReturnType<typeof readSnapshot>;
    try {
      res = readSnapshot(r, (t) => this.worlds.get(t));
    } catch {
      return;
    }
    if (!res) return;
    const { packet, world } = res;
    this.statWindow.snaps++;
    this.statWindow.bytes += data.length;
    if (packet.tick <= this.latestTick) return;
    if (this.statWindow.lastTick && packet.tick > this.statWindow.lastTick + 1) this.statWindow.missing += packet.tick - this.statWindow.lastTick - 1;
    this.statWindow.lastTick = packet.tick;
    this.latestTick = packet.tick;
    this.serverTick = packet.tick;
    this.worlds.set(packet.tick, world);
    for (const t of this.worlds.keys()) if (t < packet.tick - 64) this.worlds.delete(t);

    const decoded = new Map<number, AnyFields>();
    for (const [id, rec] of world) decoded.set(id, decodeRecord(rec));
    this.snaps.push({ tick: packet.tick, world, decoded });
    if (this.snaps.length > SNAP_BUFFER) this.snaps.shift();

    // Server clock estimate (ticks): follow early (least-delayed) arrivals quickly, late ones slowly.
    const nowTicks = (this.now() * TICK_RATE) / 1000;
    const sample = packet.tick - nowTicks;
    if (this.tickOffset === undefined || Math.abs(sample - this.tickOffset) > 15) this.tickOffset = sample;
    else this.tickOffset += (sample - this.tickOffset) * (sample > this.tickOffset ? 0.2 : 0.02);

    if (packet.echoTime > 0) {
      const rtt = this.clientTime() - packet.echoTime - packet.echoHold;
      if (rtt >= 0 && rtt < 5000) this.stats.rtt = this.stats.rtt ? this.stats.rtt * 0.9 + rtt * 0.1 : rtt;
    }
    if (this.now() - this.lastRttReport > 2000) {
      this.lastRttReport = this.now();
      this.send({ t: "rtt", ms: Math.round(this.stats.rtt) });
    }

    // Time dilation keeps the server's input buffer ~2 deep.
    this.lastQueue = this.lastQueue * 0.9 + packet.queue * 0.1;
    this.stats.serverQueue = this.lastQueue;
    this.timeScale = this.lastQueue > 3.2 ? 0.97 : this.lastQueue < 1.2 ? 1.03 : 1;

    for (const e of packet.events) {
      const key = `${e.tick}:${e.id}`;
      if (this.seenEvents.has(key)) continue;
      this.seenEvents.add(key);
      this.seenOrder.push(key);
      if (this.seenOrder.length > 2000) this.seenEvents.delete(this.seenOrder.shift()!);
      this.onEvent?.(e);
    }

    const m = decoded.get(MATCH_NET_ID);
    if (m) this.applyMatch(m);
    this.applyConstructObstacles(decoded);
    if (packet.local) this.reconcile(packet.local, decoded.get(packet.local.netId));
  }

  /** The payload, spawn gates, spawn sides and stage, exactly as the server has them this tick. */
  private applyMatch(m: AnyFields) {
    convoyWorld(this.predict, { mode: m.mode as GameMode, phase: m.phase as Phase, attacker: m.attacker!, cartMm: m.cart!, checkpoint: m.checkpoint!, stage: m.stage });
  }

  private applyConstructObstacles(decoded: Map<number, AnyFields>, ageOffsetTicks = 0) {
    this.constructDecoded = decoded;
    this.predict.physics.obstacles = [...this.predict.baseObstacles, ...constructObstaclesFromDecoded(decoded, ageOffsetTicks)];
  }

  private ageConstructObstacles(ageOffsetTicks: number) {
    this.predict.physics.obstacles = [...this.predict.baseObstacles, ...constructObstaclesFromDecoded(this.constructDecoded, ageOffsetTicks)];
  }

  private ensureLocal(netId: number, rec: AnyFields | undefined): { eid: number; reset: boolean } {
    const sim = this.predict;
    let reset = false;
    if (this.localEid !== undefined && this.localNetId !== netId) {
      sim.removePlayer(this.localEid);
      this.localEid = undefined;
    }
    if (this.localEid === undefined) {
      this.localNetId = netId;
      this.localEid = sim.addPlayer((rec?.team ?? Team.A) as Team, (rec?.hero ?? HERO_LIST[0]!.id) as HeroId, "me", false, netId);
      reset = true;
    }
    const eid = this.localEid;
    if (rec) {
      if (rec.hero !== sim.c.Player.hero[eid]) {
        sim.setHero(eid, rec.hero as HeroId);
        reset = true;
      }
      if ((rec.form ?? 0) !== sim.c.Player.form[eid]) {
        sim.setForm(eid, rec.form ?? 0, { hp: "keep" });
        reset = true;
      }
      sim.c.Player.baseHero[eid] = (rec.baseHero ?? rec.hero) as HeroId;
      sim.c.Player.disguiseTicks[eid] = rec.disguised ? Math.max(1, sim.c.Player.disguiseTicks[eid]!) : 0;
      if (rec.alive !== sim.c.Player.alive[eid]) reset = true;
      sim.c.Player.team[eid] = rec.team!;
      sim.c.Player.alive[eid] = rec.alive!;
      sim.c.Health.hp[eid] = rec.hp!;
      sim.c.Health.armor[eid] = rec.armor!;
      sim.c.Health.shield[eid] = rec.shield!;
    }
    return { eid, reset };
  }

  private reconcile(local: NonNullable<SnapshotPacket["local"]>, rec: AnyFields | undefined) {
    this.latestLocal = local;
    const sim = this.predict;
    const { eid, reset } = this.ensureLocal(local.netId, rec);
    const before = sim.posOf(eid);
    applyLocal(sim, eid, local);
    this.pending = this.pending.filter((i) => seqGreater(i.seq, local.ackSeq));
    sim.replaying = true;
    try {
      for (let i = 0; i < this.pending.length; i++) {
        this.ageConstructObstacles(i);
        sim.stepPlayer(eid, this.pending[i]!);
      }
    } finally {
      sim.replaying = false;
    }
    const after = sim.posOf(eid);
    const dx = before.x - after.x;
    const dy = before.y - after.y;
    const dz = before.z - after.z;
    const err = Math.hypot(dx, dy, dz);
    this.stats.lastCorrection = err;
    if (err > 0.001 && !reset) {
      this.stats.corrections++;
      this.onCorrection?.(dx, dy, dz);
    }
    this.stats.pendingInputs = this.pending.length;
  }

  // ---------------------------------------------------------------- per-frame update

  /** Estimated current server tick (fractional). */
  estServerTick(): number {
    if (this.tickOffset === undefined) return this.latestTick;
    return (this.now() * TICK_RATE) / 1000 + this.tickOffset;
  }

  /** Tick that remote entities are rendered at. */
  renderTick(): number {
    return this.estServerTick() - INTERP_DELAY_TICKS;
  }

  /** Advance local fixed-step prediction with the current input state. Returns ticks run. */
  update(dtSeconds: number, sample: InputSample): number {
    this.updateStats();
    if (this.localEid === undefined) return 0;
    this.accumulator += Math.min(0.25, dtSeconds) * this.timeScale;
    const step = 1 / TICK_RATE;
    let n = 0;
    while (this.accumulator >= step) {
      this.accumulator -= step;
      this.tickOnce(sample);
      n++;
    }
    return n;
  }

  /** Fraction (0..1) toward the next local prediction tick, for render smoothing. */
  get alpha() {
    return this.accumulator * TICK_RATE;
  }

  tickOnce(sample: InputSample) {
    const eid = this.localEid;
    if (eid === undefined) return;
    this.seq = (this.seq + 1) & 0xffff;
    const input = quantizeInput({ seq: this.seq, ...sample, renderTick: Math.max(0, this.renderTick()) });
    const sim = this.predict;
    this.ageConstructObstacles(this.pending.length);
    sim.stepPlayer(eid, input);
    for (const e of sim.events) this.onPredictedEvent?.(e);
    sim.events.length = 0;
    this.pending.push(input);
    if (this.pending.length > 120) this.pending.shift();
    this.sentHistory.unshift(input);
    if (this.sentHistory.length > INPUT_REDUNDANCY) this.sentHistory.length = INPUT_REDUNDANCY;
    const w = new BitWriter(128);
    writeInputPacket(w, { ackTick: this.latestTick, clientTime: this.clientTime(), inputs: this.sentHistory });
    this.transport.sendUnreliable(w.finish());
  }

  private updateStats() {
    const now = this.now();
    const el = (now - this.statWindow.start) / 1000;
    if (el >= 1) {
      this.stats.snapshotsPerSec = this.statWindow.snaps / el;
      this.stats.bytesPerSec = this.statWindow.bytes / el;
      const expected = this.statWindow.snaps + this.statWindow.missing;
      this.stats.lossPct = expected ? (100 * this.statWindow.missing) / expected : 0;
      this.stats.timeScale = this.timeScale;
      this.statWindow = { start: now, snaps: 0, bytes: 0, missing: 0, lastTick: this.statWindow.lastTick };
    }
  }

  // ---------------------------------------------------------------- interpolation

  /** Interpolated view of all replicated entities at `tick` (defaults to renderTick()). */
  interpolate(tick = this.renderTick()): Map<number, InterpEntity> {
    const out = new Map<number, InterpEntity>();
    const snaps = this.snaps;
    if (!snaps.length) return out;
    let a = snaps.at(-1)!;
    let b = a;
    if (tick < snaps[0]!.tick) a = b = snaps[0]!;
    else
      for (let i = snaps.length - 1; i >= 0; i--) {
        if (snaps[i]!.tick <= tick) {
          a = snaps[i]!;
          b = snaps[i + 1] ?? snaps[i]!;
          break;
        }
      }
    const t = b.tick > a.tick ? Math.max(0, Math.min(1, (tick - a.tick) / (b.tick - a.tick))) : 0;
    for (const [id, fb] of b.decoded) {
      const fa = a.decoded.get(id);
      const rec = b.world.get(id)!;
      if (!fa || a.world.get(id)!.type !== rec.type) {
        out.set(id, { netId: id, type: rec.type, f: fb });
        continue;
      }
      const f: AnyFields = { ...fb };
      for (const k of LERP_FIELDS[rec.type]) f[k] = lerp(fa[k]!, fb[k]!, t);
      if (rec.type === EntityType.Player) {
        f.yaw = lerpAngle(fa.yaw!, fb.yaw!, t);
        if (!fa.alive || !fb.alive) {
          f.x = fb.x!;
          f.y = fb.y!;
          f.z = fb.z!;
        }
      }
      out.set(id, { netId: id, type: rec.type, f });
    }
    return out;
  }

  /** Latest (non-interpolated) decoded state — HUD values, match state. */
  latest(): Map<number, AnyFields> | undefined {
    return this.snaps.at(-1)?.decoded;
  }

  close() {
    this.transport.close();
    this.predict.physics.free();
  }
}

const LERP_FIELDS: Record<EntityType, string[]> = {
  [EntityType.Player]: ["x", "y", "z", "pitch"],
  [EntityType.Projectile]: ["x", "y", "z"],
  [EntityType.Zone]: ["x", "y", "z", "radius"],
  [EntityType.Match]: ["progA", "progB", "capture", "cart"],
  [EntityType.Construct]: ["x", "y", "z"],
};
